import { useState } from "react";
import type { CSSProperties } from "react";
import type { AnswerOptionType, QuestionOption } from "../../../shared/types";
import { ShapeRenderer } from "./ShapeRenderer";

type NormalizedOption = {
  type: AnswerOptionType | "unknown";
  label: string;
  value?: string | number;
  visual: Record<string, unknown>;
};

const dotPositions: Record<string, string> = {
  "top-left": "left-2 top-2",
  top: "left-1/2 top-2 -translate-x-1/2",
  "top-right": "right-2 top-2",
  left: "left-2 top-1/2 -translate-y-1/2",
  "mid-left": "left-2 top-1/2 -translate-y-1/2",
  center: "left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2",
  right: "right-2 top-1/2 -translate-y-1/2",
  "mid-right": "right-2 top-1/2 -translate-y-1/2",
  "bottom-left": "bottom-2 left-2",
  bottom: "bottom-2 left-1/2 -translate-x-1/2",
  "bottom-right": "bottom-2 right-2"
};

function warnInvalidOption(option: QuestionOption, reason: string) {
  const isDev = Boolean((import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV);
  if (isDev) {
    console.warn(`[Brain Test] Invalid answer option ${option.id}: ${reason}`, option);
  }
}

function normalizeOption(option: QuestionOption): NormalizedOption {
  const visual = option.visual ?? option.visualSpec ?? {};
  const visualKind = typeof visual.kind === "string" ? visual.kind : undefined;
  const type = option.type ?? visualKind;

  if (
    type === "text" ||
    type === "number" ||
    type === "symbolChoice" ||
    type === "shape" ||
    type === "gridPosition" ||
    type === "dotTile" ||
    type === "dotBlock" ||
    type === "circleHalfFill" ||
    type === "circleSegmentUnion" ||
    type === "pieSlice" ||
    type === "topViewGrid" ||
    type === "symbolGridCard" ||
    type === "blueCircleDotCard" ||
    type === "colorGridTile" ||
    type === "roundedDome" ||
    type === "quadrantColorShape" ||
    type === "miniGridSymbolCycle" ||
    type === "miniGridSymbol" ||
    type === "hexCross" ||
    type === "blockGrid" ||
    type === "tileSplit" ||
    type === "lineTile" ||
    type === "nestedShape" ||
    type === "quadrantShape" ||
    type === "figurePiece" ||
    type === "image"
  ) {
    return { type, label: option.label, value: option.value, visual };
  }

  if (typeof visual.shape === "string") return { type: "shape", label: option.label, value: option.value, visual };
  if (typeof visual.src === "string") return { type: "image", label: option.label, value: option.value, visual };
  if (option.value !== undefined) return { type: typeof option.value === "number" ? "number" : "text", label: option.label, value: option.value, visual };

  return { type: "text", label: option.label, visual };
}

function TextOptionRenderer({ option }: { option: NormalizedOption }) {
  const text = String(option.value ?? option.label);
  return <span className="block w-full text-left text-base font-semibold leading-snug text-white sm:text-center">{text}</span>;
}

function NumberOptionRenderer({ option }: { option: NormalizedOption }) {
  const value = String(option.value ?? option.label);
  return <span className="text-2xl font-bold tabular-nums text-cyan-50">{value}</span>;
}

function SymbolChoiceOptionRenderer({ option }: { option: NormalizedOption }) {
  const value = String(option.value ?? option.label);
  return <span className="text-4xl font-black leading-none text-cyan-50 sm:text-5xl">{value}</span>;
}

function ShapeOptionRenderer({ option, source }: { option: NormalizedOption; source: QuestionOption }) {
  if (!option.visual.shape) {
    warnInvalidOption(source, "shape option is missing visual.shape");
    return <UnavailableOption />;
  }
  return <ShapeRenderer spec={option.visual} />;
}

function GridPositionOptionRenderer({ option, source }: { option: NormalizedOption; source: QuestionOption }) {
  const size = Number(option.visual.size ?? option.visual.gridSize ?? 3);
  const activeCell = Array.isArray(option.visual.activeCell) ? option.visual.activeCell : null;
  const activeIndex =
    typeof option.visual.activeIndex === "number"
      ? option.visual.activeIndex
      : activeCell && typeof activeCell[0] === "number" && typeof activeCell[1] === "number"
        ? activeCell[0] * size + activeCell[1]
        : undefined;

  if (!Number.isFinite(size) || size < 2 || typeof activeIndex !== "number") {
    warnInvalidOption(source, "gridPosition option is missing grid size or active cell");
    return <UnavailableOption />;
  }

  return (
    <span
      className="grid aspect-square w-24 max-w-full overflow-hidden rounded-md border border-cyan-100/70 bg-cyan-100/70 p-[2px]"
      style={{ gridTemplateColumns: `repeat(${size}, minmax(0, 1fr))`, gridTemplateRows: `repeat(${size}, minmax(0, 1fr))`, gap: 2 }}
    >
      {Array.from({ length: size * size }, (_, cell) => (
        <span key={cell} className="flex min-h-0 min-w-0 items-center justify-center bg-slate-950/90">
          {cell === activeIndex ? <span className="block h-3.5 w-3.5 rounded-sm bg-cyan-200 shadow-[0_0_14px_rgb(103_232_249_/_0.65)]" /> : null}
        </span>
      ))}
    </span>
  );
}

function DotTileOptionRenderer({ option, source }: { option: NormalizedOption; source: QuestionOption }) {
  const dots = Array.isArray(option.visual.dots) ? option.visual.dots.map(String) : [];
  if (dots.length === 0) {
    warnInvalidOption(source, "dotTile option is missing dots");
    return <UnavailableOption />;
  }

  return (
    <span className="relative block aspect-square w-24 max-w-full rounded-lg border border-cyan-200/25 bg-slate-950/55 shadow-inner shadow-cyan-950/20">
      {dots.map((position, index) => {
        const className = dotPositions[position];
        if (!className) {
          warnInvalidOption(source, `unsupported dot position ${position}`);
          return null;
        }
        return <span key={`${position}-${index}`} className={`absolute h-3 w-3 rounded-full bg-cyan-200 shadow-[0_0_12px_rgb(103_232_249_/_0.55)] ${className}`} />;
      })}
    </span>
  );
}

function DotBlockOptionRenderer({ option, source }: { option: NormalizedOption; source: QuestionOption }) {
  const dots = Array.isArray(option.visual.dots) ? option.visual.dots.map(String) : [];
  const showGrid = option.visual.showGrid === true;
  if (dots.length === 0) {
    warnInvalidOption(source, "dotBlock option is missing dots");
    return <UnavailableOption />;
  }

  const positionStyle = (position: string) => {
    const normalizedPosition =
      {
        "1": "top-left",
        "2": "top",
        "3": "top-right",
        "4": "left",
        "5": "center",
        "6": "right",
        "7": "bottom-left",
        "8": "bottom",
        "9": "bottom-right"
      }[position] ?? position;
    const center = "calc(50% - 7px)";
    const near = "calc(16.6667% - 7px)";
    const far = "calc(83.3333% - 7px)";
    const middleA = "calc(33.3333% - 7px)";
    const middleB = "calc(66.6667% - 7px)";
    const map: Record<string, CSSProperties> = {
      center: { top: center, left: center },
      "top-left": { top: near, left: near },
      top: { top: near, left: center },
      "top-right": { top: near, left: far },
      left: { top: center, left: near },
      right: { top: center, left: far },
      "bottom-left": { top: far, left: near },
      bottom: { top: far, left: center },
      "bottom-right": { top: far, left: far },
      "left-upper": { top: near, left: near },
      "left-lower": { top: far, left: near },
      "right-upper": { top: near, left: far },
      "right-lower": { top: far, left: far }
    };
    return map[normalizedPosition] ?? map.center;
  };

  return (
    <span
      className={`relative block aspect-square w-28 max-w-full overflow-hidden rounded-lg bg-slate-950/70 shadow-inner shadow-cyan-950/20 ${
        showGrid ? "border border-cyan-100/55" : "border border-cyan-200/35"
      }`}
    >
      {showGrid ? (
        <span className="pointer-events-none absolute inset-0 z-0 rounded-lg">
          <span className="absolute bottom-0 left-1/3 top-0 w-[2px] bg-cyan-200/40" />
          <span className="absolute bottom-0 left-2/3 top-0 w-[2px] bg-cyan-200/40" />
          <span className="absolute left-0 right-0 top-1/3 h-[2px] bg-cyan-200/40" />
          <span className="absolute left-0 right-0 top-2/3 h-[2px] bg-cyan-200/40" />
        </span>
      ) : null}
      {dots.map((position, index) => (
        <span
          key={`${position}-${index}`}
          className="absolute z-10 h-3.5 w-3.5 rounded-full bg-cyan-200 shadow-[0_0_14px_rgb(103_232_249_/_0.65)]"
          style={positionStyle(position)}
        />
      ))}
    </span>
  );
}

function circleFillColor(color: string) {
  const colors: Record<string, string> = {
    blue: "rgb(56 189 248)",
    green: "rgb(52 211 153)",
    orange: "rgb(251 146 60)"
  };
  return colors[color] ?? colors.blue;
}

function CircleHalfFillOptionRenderer({ option }: { option: NormalizedOption }) {
  const color = circleFillColor(String(option.visual.color ?? "blue"));
  const line = String(option.visual.line ?? "vertical");
  const fillShape =
    line === "horizontal" ? (
      <path d="M16 16H84V50H16Z" fill={color} clipPath="url(#circle-option-clip)" />
    ) : line === "diagonal" ? (
      <path d="M16 16H84L16 84Z" fill={color} clipPath="url(#circle-option-clip)" />
    ) : (
      <path d="M16 16H50V84H16Z" fill={color} clipPath="url(#circle-option-clip)" />
    );
  const separator =
    line === "horizontal" ? <line x1="18" y1="50" x2="82" y2="50" /> : line === "diagonal" ? <line x1="0" y1="100" x2="100" y2="0" clipPath="url(#circle-option-clip)" /> : <line x1="50" y1="18" x2="50" y2="82" />;

  return (
    <svg className="h-28 w-28 max-w-full" viewBox="0 0 100 100" role="img" aria-label="Κύκλος με χρώμα και γραμμή">
      <defs>
        <clipPath id="circle-option-clip">
          <circle cx="50" cy="50" r="34" />
        </clipPath>
      </defs>
      <circle cx="50" cy="50" r="34" fill="rgb(15 23 42)" stroke="rgb(148 163 184)" strokeWidth="3" />
      {fillShape}
      <g stroke="rgb(226 232 240)" strokeWidth="4" strokeLinecap="round">
        {separator}
      </g>
      <circle cx="50" cy="50" r="34" fill="none" stroke="rgb(203 213 225)" strokeWidth="3" />
    </svg>
  );
}

function MiniGridSymbolOptionRenderer({ option }: { option: NormalizedOption }) {
  const symbol = String(option.visual.symbol ?? "diamond");
  const filled = new Set(Array.isArray(option.visual.filled) ? option.visual.filled.map(String) : []);
  const symbolCells = new Set(Array.isArray(option.visual.symbolCells) ? option.visual.symbolCells.map(String) : [...filled]);
  const cells = ["tl", "tr", "bl", "br"];
  const glyph = symbol === "star" ? "★" : symbol === "circle" ? "●" : "◆";

  return (
    <span className="grid aspect-square w-28 max-w-full grid-cols-2 overflow-hidden rounded-lg border border-cyan-100/45 bg-slate-200 p-1">
      {cells.map((cell) => {
        const isFilled = filled.has(cell);
        return (
          <span key={cell} className={`flex items-center justify-center border border-slate-500/60 text-2xl font-black leading-none ${isFilled ? "bg-slate-950 text-white" : "bg-slate-100 text-slate-950"}`}>
            {symbolCells.has(cell) ? glyph : null}
          </span>
        );
      })}
    </span>
  );
}

function optionSet(value: unknown) {
  return new Set(Array.isArray(value) ? value.map(String) : []);
}

function CircleSegmentUnionOptionRenderer({ option }: { option: NormalizedOption }) {
  const segments = optionSet(option.visual.segments);
  const centerBlack = option.visual.centerBlack === true;
  const segmentPaths = {
    top: "M-4 -4H104L65 42H35Z",
    right: "M104 -4V104L58 65V35Z",
    bottom: "M104 104H-4L35 58H65Z",
    left: "M-4 104V-4L42 35V65Z"
  };
  const dividerLines = [
    { x1: 20.302, y1: 20.302, x2: 38, y2: 38 },
    { x1: 79.698, y1: 20.302, x2: 62, y2: 38 },
    { x1: 79.698, y1: 79.698, x2: 62, y2: 62 },
    { x1: 20.302, y1: 79.698, x2: 38, y2: 62 }
  ];

  return (
    <svg className="h-28 w-28 max-w-full rounded-sm bg-white p-1" viewBox="0 0 100 100" role="img" aria-label="Επιλογή κύκλου με τμήματα" shapeRendering="geometricPrecision">
      <defs>
        <clipPath id="circle-segment-option-clip">
          <circle cx="50" cy="50" r="42" />
        </clipPath>
      </defs>
      <circle cx="50" cy="50" r="42" fill="rgb(255 255 255)" />
      <g clipPath="url(#circle-segment-option-clip)">
        {Object.entries(segmentPaths).map(([id, d]) => (
          <path key={id} d={d} fill={segments.has(id) ? "rgb(0 0 0)" : "rgb(255 255 255)"} />
        ))}
      </g>
      <g stroke="rgb(0 0 0)" strokeWidth="2.4" strokeLinecap="butt">
        {dividerLines.map((line, index) => (
          <line key={index} {...line} />
        ))}
      </g>
      <rect x="38" y="38" width="24" height="24" fill={centerBlack ? "rgb(0 0 0)" : "rgb(255 255 255)"} stroke="rgb(0 0 0)" strokeWidth="2.8" />
      <circle cx="50" cy="50" r="42" fill="none" stroke="rgb(0 0 0)" strokeWidth="3" />
    </svg>
  );
}

const optionPieSlices = ["top", "top-right", "right", "bottom-right", "bottom", "bottom-left", "left", "top-left"];

function optionPolarPoint(cx: number, cy: number, radius: number, angle: number) {
  const radians = ((angle - 90) * Math.PI) / 180;
  return { x: cx + radius * Math.cos(radians), y: cy + radius * Math.sin(radians) };
}

function optionPieSlicePath(index: number) {
  const start = optionPolarPoint(50, 50, 38, index * 45 - 22.5);
  const end = optionPolarPoint(50, 50, 38, index * 45 + 22.5);
  return `M50 50L${start.x.toFixed(3)} ${start.y.toFixed(3)}A38 38 0 0 1 ${end.x.toFixed(3)} ${end.y.toFixed(3)}Z`;
}

function PieSliceOptionRenderer({ option }: { option: NormalizedOption }) {
  const slices = optionSet(option.visual.slices);
  return (
    <svg className="h-28 w-28 max-w-full rounded-sm bg-white p-1" viewBox="0 0 100 100" role="img" aria-label="Επιλογή κύκλου με οκτώ τμήματα" shapeRendering="geometricPrecision">
      <circle cx="50" cy="50" r="38" fill="rgb(255 255 255)" />
      {optionPieSlices.map((slice, index) => (
        <path key={slice} d={optionPieSlicePath(index)} fill={slices.has(slice) ? "rgb(45 212 191)" : "rgb(255 255 255)"} />
      ))}
      {Array.from({ length: 8 }, (_, index) => {
        const point = optionPolarPoint(50, 50, 38, index * 45 - 22.5);
        return <line key={index} x1="50" y1="50" x2={point.x} y2={point.y} stroke="rgb(0 0 0)" strokeWidth="1.6" />;
      })}
      <circle cx="50" cy="50" r="38" fill="none" stroke="rgb(0 0 0)" strokeWidth="2.6" />
    </svg>
  );
}

function topViewColor(color: string) {
  const colors: Record<string, string> = {
    red: "#ef1b22",
    green: "#0b9446",
    yellow: "#f3e71a",
    orange: "#ee7c1c",
    blue: "#20a7dd"
  };
  return colors[color] ?? colors.red;
}

function TopViewGridOptionRenderer({ option, source }: { option: NormalizedOption; source: QuestionOption }) {
  const grid = Array.isArray(option.visual.grid) ? (option.visual.grid as unknown[]).map((row) => (Array.isArray(row) ? row.map(String) : [])) : [];
  if (grid.length !== 3 || grid.some((row) => row.length !== 3)) {
    warnInvalidOption(source, "topViewGrid option must include a 3x3 grid");
    return <UnavailableOption />;
  }

  return (
    <span className="grid aspect-square w-28 max-w-full grid-cols-3 overflow-hidden rounded-md border-2 border-slate-950 bg-slate-950 p-px">
      {grid.flat().map((color, index) => (
        <span key={`${color}-${index}`} className="block min-h-0 min-w-0 border border-slate-950" style={{ backgroundColor: topViewColor(color) }} />
      ))}
    </span>
  );
}

function SymbolGridIcon({ symbol }: { symbol: string }) {
  if (symbol === "C") return <span className="block h-4 w-4 rounded-full bg-black sm:h-5 sm:w-5" />;
  if (symbol === "D") return <span className="block h-4 w-4 rotate-45 bg-black sm:h-5 sm:w-5" />;
  if (symbol === "P") {
    return (
      <svg className="h-6 w-6 sm:h-7 sm:w-7" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 2 22 9.2 18.2 21H5.8L2 9.2Z" fill="rgb(0 0 0)" />
      </svg>
    );
  }
  return (
    <svg className="h-6 w-6 sm:h-7 sm:w-7" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3 22 21H2Z" fill="rgb(0 0 0)" />
    </svg>
  );
}

function SymbolGridCardOptionRenderer({ option, source }: { option: NormalizedOption; source: QuestionOption }) {
  const grid = Array.isArray(option.visual.grid) ? (option.visual.grid as unknown[]).map((row) => (Array.isArray(row) ? row.map(String) : [])) : [];
  if (grid.length !== 3 || grid.some((row) => row.length !== 3)) {
    warnInvalidOption(source, "symbolGridCard option must include a 3x3 grid");
    return <UnavailableOption />;
  }

  return (
    <span className="grid aspect-square w-28 max-w-full grid-cols-3 overflow-hidden rounded-md border-2 border-slate-900 bg-slate-300 p-px shadow-sm">
      {grid.flat().map((symbol, index) => (
        <span key={`${symbol}-${index}`} className="flex min-h-0 min-w-0 items-center justify-center border border-slate-300 bg-slate-50">
          <SymbolGridIcon symbol={symbol} />
        </span>
      ))}
    </span>
  );
}

function BlueCircleDotOptionRenderer({ option }: { option: NormalizedOption }) {
  const dots = option.visual.dots && typeof option.visual.dots === "object" ? (option.visual.dots as Record<string, unknown>) : option.visual;
  const positions = [
    { id: "top", cx: 50, cy: 24 },
    { id: "right", cx: 76, cy: 50 },
    { id: "bottom", cx: 50, cy: 76 },
    { id: "left", cx: 24, cy: 50 },
    { id: "center", cx: 50, cy: 50 }
  ] as const;
  const fill = (state: string) => (state === "gold" ? "rgb(245 196 45)" : "rgb(15 23 42)");

  return (
    <svg className="h-28 w-28 max-w-full rounded-sm bg-white p-1" viewBox="0 0 100 100" role="img" aria-label="Επιλογή μπλε κύκλου με κουκίδες">
      <circle cx="50" cy="50" r="40" fill="rgb(59 130 246)" stroke="rgb(15 23 42)" strokeWidth="2.6" />
      {positions.map((position) => {
        const state = String(dots[position.id] ?? "empty");
        if (state === "empty") return null;
        return <circle key={position.id} cx={position.cx} cy={position.cy} r="7" fill={fill(state)} stroke="rgb(15 23 42)" strokeWidth="1.2" />;
      })}
    </svg>
  );
}

function colorGridOptionFill(color: string) {
  const colors: Record<string, string> = {
    P: "#9b4dff",
    B: "#2f86ff",
    K: "#05070b",
    L: "#a2ef28"
  };
  return colors[color] ?? colors.P;
}

function ColorGridTileOptionRenderer({ option, source }: { option: NormalizedOption; source: QuestionOption }) {
  const grid = Array.isArray(option.visual.grid) ? (option.visual.grid as unknown[]).map((row) => (Array.isArray(row) ? row.map(String) : [])) : [];
  if (grid.length !== 3 || grid.some((row) => row.length !== 3)) {
    warnInvalidOption(source, "colorGridTile option must include a 3x3 grid");
    return <UnavailableOption />;
  }

  return (
    <span className="grid aspect-square w-28 max-w-full grid-cols-3 overflow-hidden rounded-md border border-cyan-100/45 bg-cyan-100/35 p-px shadow-[0_0_14px_rgb(34_211_238_/_0.16)]">
      {grid.flat().map((color, index) => (
        <span key={`${color}-${index}`} className="block min-h-0 min-w-0 border border-slate-200/45" style={{ backgroundColor: colorGridOptionFill(color) }} />
      ))}
    </span>
  );
}

function roundedDomeFill(color: string) {
  const colors: Record<string, string> = {
    purple: "rgb(126 87 194)",
    orange: "rgb(249 115 22)",
    lime: "rgb(132 204 22)",
    mutedOrange: "rgb(180 116 64)"
  };
  return colors[color] ?? colors.purple;
}

function RoundedDomeOptionRenderer({ option }: { option: NormalizedOption }) {
  const color = String(option.visual.color ?? "purple");
  const rotation = option.visual.variant === "rotated" ? 90 : 0;
  return (
    <svg className="h-28 w-28 max-w-full rounded-sm bg-white p-1" viewBox="0 0 100 100" role="img" aria-label="Επιλογή στρογγυλεμένου σχήματος" shapeRendering="geometricPrecision">
      <g transform={`rotate(${rotation} 50 50)`}>
        <path d="M50 10C72 10 90 28 90 50V88H26C17 88 10 81 10 72V50C10 28 28 10 50 10Z" fill={roundedDomeFill(color)} stroke="rgb(15 23 42)" strokeOpacity="0.12" strokeWidth="2" />
      </g>
    </svg>
  );
}

function quadrantOptionFill(color: string) {
  const colors: Record<string, string> = {
    red: "rgb(239 68 68)",
    green: "rgb(34 197 94)",
    blue: "rgb(37 99 235)",
    yellow: "rgb(250 204 21)"
  };
  return colors[color] ?? colors.red;
}

function quadrantOptionShapeElement(shape: string) {
  if (shape === "circle") return <circle cx="50" cy="50" r="38" />;
  if (shape === "diamond") return <polygon points="50 8 92 50 50 92 8 50" />;
  if (shape === "octagon") return <polygon points="32 10 68 10 90 32 90 68 68 90 32 90 10 68 10 32" />;
  return <rect x="12" y="12" width="76" height="76" />;
}

function QuadrantColorShapeOptionRenderer({ option }: { option: NormalizedOption }) {
  const shape = String(option.visual.shape ?? "octagon");
  const quadrants = option.visual.quadrants && typeof option.visual.quadrants === "object" ? (option.visual.quadrants as Record<string, unknown>) : {};
  const clipId = `quadrant-option-${shape}-${String(option.label).replace(/[^a-z0-9_-]/gi, "")}`;
  const cells = [
    { id: "tl", x: 0, y: 0, color: String(quadrants.tl ?? "green") },
    { id: "tr", x: 50, y: 0, color: String(quadrants.tr ?? "yellow") },
    { id: "bl", x: 0, y: 50, color: String(quadrants.bl ?? "blue") },
    { id: "br", x: 50, y: 50, color: String(quadrants.br ?? "red") }
  ];

  return (
    <svg className="h-28 w-28 max-w-full rounded-sm bg-white p-1" viewBox="0 0 100 100" role="img" aria-label="Επιλογή σχήματος με χρωματιστά τεταρτημόρια" shapeRendering="geometricPrecision">
      <defs>
        <clipPath id={clipId}>{quadrantOptionShapeElement(shape)}</clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        {cells.map((cell) => (
          <rect key={cell.id} x={cell.x} y={cell.y} width="50" height="50" fill={quadrantOptionFill(cell.color)} />
        ))}
      </g>
      <g clipPath={`url(#${clipId})`} stroke="rgb(0 0 0)" strokeWidth="2.2">
        <line x1="50" y1="0" x2="50" y2="100" />
        <line x1="0" y1="50" x2="100" y2="50" />
      </g>
      <g fill="none" stroke="rgb(0 0 0)" strokeWidth="2.8">{quadrantOptionShapeElement(shape)}</g>
    </svg>
  );
}

function CycleOptionSymbolIcon({ symbol }: { symbol: string }) {
  if (symbol === "R") return <span className="block h-4 w-4 rounded-full bg-rose-500" />;
  if (symbol === "B") {
    return (
      <svg className="h-5 w-5" viewBox="0 0 20 20" aria-hidden="true">
        <path d="M10 2 18 17H2Z" fill="rgb(14 165 233)" />
      </svg>
    );
  }
  return <span className="block h-4 w-4 bg-yellow-300" />;
}

function MiniGridSymbolCycleOptionRenderer({ option }: { option: NormalizedOption }) {
  const grid = Array.isArray(option.visual.grid) ? (option.visual.grid as unknown[]).map((row) => (Array.isArray(row) ? row.map(String) : [])) : [];
  const rows = grid.length ? grid : [["Y", "R", "B"], ["B", "Y", "R"], ["R", "B", "Y"]];
  return (
    <span className="grid aspect-square w-28 max-w-full grid-cols-3 overflow-hidden rounded-md border border-slate-700 bg-slate-700 p-px">
      {rows.flat().map((symbol, index) => (
        <span key={`${symbol}-${index}`} className="flex min-h-0 min-w-0 items-center justify-center border border-slate-300 bg-white">
          <CycleOptionSymbolIcon symbol={symbol} />
        </span>
      ))}
    </span>
  );
}

function HexCrossShape({ active }: { active: string[] }) {
  const activeSet = new Set(active.map(String));
  const positions = [
    { id: "top", cx: 50, cy: 25 },
    { id: "left", cx: 30, cy: 58 },
    { id: "right", cx: 70, cy: 58 },
    { id: "bottom", cx: 50, cy: 91 }
  ];
  const points = (cx: number, cy: number) => `${cx},${cy - 23} ${cx + 20},${cy - 11.5} ${cx + 20},${cy + 11.5} ${cx},${cy + 23} ${cx - 20},${cy + 11.5} ${cx - 20},${cy - 11.5}`;

  return (
    <svg className="h-28 w-28 max-w-full overflow-visible" viewBox="0 0 100 116" role="img" aria-label="Σχήμα από τέσσερα εξάγωνα" shapeRendering="geometricPrecision">
      <g stroke="rgb(248 250 252)" strokeWidth="8" strokeLinejoin="miter" strokeMiterlimit="2">
        {positions.map((position) => (
          <polygon key={`back-${position.id}`} points={points(position.cx, position.cy)} fill={activeSet.has(position.id) ? "rgb(2 6 23)" : "rgb(255 255 255)"} />
        ))}
      </g>
      <g stroke="rgb(0 0 0)" strokeWidth="4" strokeLinejoin="miter" strokeMiterlimit="2">
        {positions.map((position) => (
          <polygon key={position.id} points={points(position.cx, position.cy)} fill={activeSet.has(position.id) ? "rgb(0 0 0)" : "rgb(255 255 255)"} />
        ))}
      </g>
    </svg>
  );
}

function HexCrossOptionRenderer({ option }: { option: NormalizedOption }) {
  const active = Array.isArray(option.visual.active) ? option.visual.active.map(String) : [];
  return <HexCrossShape active={active} />;
}

function BlockGridOptionRenderer({ option, source }: { option: NormalizedOption; source: QuestionOption }) {
  const size = Number(option.visual.gridSize ?? 3);
  const filledCells = Array.isArray(option.visual.filledCells) ? option.visual.filledCells.map(Number) : [];
  const filled = new Set(filledCells.filter((cell) => Number.isInteger(cell) && cell >= 0 && cell < size * size));

  if (!Number.isFinite(size) || size < 2 || filled.size === 0) {
    warnInvalidOption(source, "blockGrid option is missing grid size or filled cells");
    return <UnavailableOption />;
  }

  return (
    <span
      className="grid aspect-square w-28 max-w-full overflow-hidden rounded-md border border-cyan-100/70 bg-cyan-100/70 p-[2px]"
      style={{ gridTemplateColumns: `repeat(${size}, minmax(0, 1fr))`, gridTemplateRows: `repeat(${size}, minmax(0, 1fr))`, gap: 2 }}
    >
      {Array.from({ length: size * size }, (_, cell) => (
        <span key={cell} className="flex min-h-0 min-w-0 items-center justify-center bg-slate-950/90">
          {filled.has(cell) ? <span className="block h-full w-full bg-cyan-200 shadow-[0_0_12px_rgb(103_232_249_/_0.35)]" /> : null}
        </span>
      ))}
    </span>
  );
}

function TileSplitOptionRenderer({ option }: { option: NormalizedOption }) {
  const orientation = Number(option.visual.orientation ?? 0);
  return (
    <svg className="h-24 w-24 max-w-full" viewBox="0 0 100 100" role="img" aria-label="Επιλογή διαγώνιου tile">
      <rect x="3" y="3" width="94" height="94" rx="7" fill="rgb(248 250 252)" stroke="rgb(148 163 184)" strokeWidth="4" />
      <g transform={`rotate(${orientation * 90} 50 50)`}>
        <polygon points="3,3 97,3 3,97" fill="rgb(2 6 23)" />
        <polygon points="97,3 97,97 3,97" fill="rgb(248 250 252)" />
        <line x1="3" y1="97" x2="97" y2="3" stroke="rgb(15 23 42)" strokeWidth="3" />
      </g>
      <rect x="3" y="3" width="94" height="94" rx="7" fill="none" stroke="rgb(148 163 184)" strokeWidth="4" />
    </svg>
  );
}

function LineTileOptionRenderer({ option }: { option: NormalizedOption }) {
  const lines = new Set(Array.isArray(option.visual.lines) ? option.visual.lines.map(String) : []);
  const strokeProps = { stroke: "rgb(103 232 249)", strokeWidth: 9, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  return (
    <svg className="h-24 w-24 max-w-full" viewBox="0 0 100 100" role="img" aria-label="Επιλογή γραμμικού tile">
      <rect x="6" y="6" width="88" height="88" rx="8" fill="rgb(2 6 23)" stroke="rgb(148 163 184)" strokeWidth="3" />
      {lines.has("top") ? <line x1="24" y1="24" x2="76" y2="24" {...strokeProps} /> : null}
      {lines.has("right") ? <line x1="76" y1="24" x2="76" y2="76" {...strokeProps} /> : null}
      {lines.has("bottom") ? <line x1="24" y1="76" x2="76" y2="76" {...strokeProps} /> : null}
      {lines.has("left") ? <line x1="24" y1="24" x2="24" y2="76" {...strokeProps} /> : null}
      {lines.has("diagDown") ? <line x1="24" y1="24" x2="76" y2="76" {...strokeProps} /> : null}
      {lines.has("diagUp") ? <line x1="24" y1="76" x2="76" y2="24" {...strokeProps} /> : null}
      {lines.has("midH") ? <line x1="24" y1="50" x2="76" y2="50" {...strokeProps} /> : null}
      {lines.has("midV") ? <line x1="50" y1="24" x2="50" y2="76" {...strokeProps} /> : null}
    </svg>
  );
}

function FigurePieceOptionRenderer({ option }: { option: NormalizedOption }) {
  const variant = String(option.visual.variant ?? "correct");
  const showCircle = variant === "correct" || variant === "marker-only" || variant === "mirrored";
  const showCorner = variant === "correct" || variant === "wrong-angle" || variant === "mirrored";
  const diagDown = variant === "wrong-diagonal";
  const cornerPath = variant === "mirrored" ? "M28 28h42v42" : "M30 72h42V30";

  return (
    <svg className="h-28 w-28 max-w-full" viewBox="0 0 100 100" role="img" aria-label="Κομμάτι συμπλήρωσης σχήματος">
      <rect x="5" y="5" width="90" height="90" rx="8" fill="rgb(2 6 23)" stroke="rgb(148 163 184)" strokeWidth="4" />
      <path d={diagDown ? "M12 12 88 88" : "M12 88 88 12"} stroke="rgb(103 232 249)" strokeWidth="7" strokeLinecap="round" />
      {showCorner ? <path d={cornerPath} stroke="rgb(103 232 249)" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" /> : null}
      {showCircle ? <circle cx="70" cy="30" r="10" fill="none" stroke="rgb(103 232 249)" strokeWidth="5" /> : null}
      {variant === "wrong-angle" ? <circle cx="30" cy="70" r="10" fill="none" stroke="rgb(103 232 249)" strokeWidth="5" /> : null}
    </svg>
  );
}

function optionPolygonPoints(shape: string) {
  if (shape === "triangle") return "50 14 88 84 12 84";
  if (shape === "invertedTriangle") return "12 16 88 16 50 86";
  if (shape === "diamond") return "50 10 90 50 50 90 10 50";
  if (shape === "octagon") return "32 10 68 10 90 32 90 68 68 90 32 90 10 68 10 32";
  return "16 16 84 16 84 84 16 84";
}

function NestedShapeOptionRenderer({ option }: { option: NormalizedOption }) {
  const outer = String(option.visual.outer ?? "triangle");
  const inner = String(option.visual.inner ?? "circle");
  const classic = option.visual.style === "classicLight";
  const tileFill = classic ? "rgb(248 250 252)" : "rgb(2 6 23)";
  const tileStroke = classic ? "rgb(15 23 42)" : "rgb(51 65 85)";
  const outerStroke = classic ? "rgb(15 23 42)" : "rgb(103 232 249)";
  const innerStroke = classic ? "rgb(15 23 42)" : "rgb(248 250 252)";
  return (
    <svg className="h-24 w-24 max-w-full" viewBox="0 0 100 100" role="img" aria-label="Επιλογή με εσωτερικό και εξωτερικό σχήμα">
      <rect x="4" y="4" width="92" height="92" rx={classic ? "0" : "8"} fill={tileFill} stroke={tileStroke} strokeWidth={classic ? "0" : "2"} />
      {outer === "circle" ? (
        <circle cx="50" cy="50" r="34" fill="none" stroke={outerStroke} strokeWidth="5" />
      ) : (
        <polygon points={optionPolygonPoints(outer)} fill="none" stroke={outerStroke} strokeWidth="5" strokeLinejoin="round" />
      )}
      {inner === "circle" ? (
        <circle cx="50" cy="50" r="13" fill="none" stroke={innerStroke} strokeWidth="4" />
      ) : (
        <polygon points={optionPolygonPoints(inner)} fill="none" stroke={innerStroke} strokeWidth="4" strokeLinejoin="round" transform="translate(25 25) scale(0.5)" />
      )}
    </svg>
  );
}

function optionQuadrantRect(region: string) {
  if (region === "top-right") return { x: 50, y: 0, width: 50, height: 50 };
  if (region === "bottom-right") return { x: 50, y: 50, width: 50, height: 50 };
  if (region === "bottom-left") return { x: 0, y: 50, width: 50, height: 50 };
  return { x: 0, y: 0, width: 50, height: 50 };
}

function QuadrantShapeOptionRenderer({ option }: { option: NormalizedOption }) {
  const shape = String(option.visual.shape ?? "square");
  const filledRegion = String(option.visual.filledRegion ?? "top-left");
  const clipId = `option-clip-${shape}-${filledRegion}`;
  const fill = optionQuadrantRect(filledRegion);
  const shapeElement = shape === "circle" ? <circle cx="50" cy="50" r="35" /> : <polygon points={optionPolygonPoints(shape)} />;

  return (
    <svg className="h-24 w-24 max-w-full" viewBox="0 0 100 100" role="img" aria-label="Επιλογή σχήματος με μαύρο τεταρτημόριο">
      <rect x="2" y="2" width="96" height="96" rx="8" fill="rgb(248 250 252)" stroke="rgb(203 213 225)" strokeWidth="2" />
      <defs>
        <clipPath id={clipId}>{shapeElement}</clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        <rect x="0" y="0" width="100" height="100" fill="rgb(248 250 252)" />
        <rect {...fill} fill="rgb(2 6 23)" />
        <line x1="50" y1="8" x2="50" y2="92" stroke="rgb(15 23 42)" strokeWidth="3" />
        <line x1="8" y1="50" x2="92" y2="50" stroke="rgb(15 23 42)" strokeWidth="3" />
      </g>
      {shape === "circle" ? (
        <circle cx="50" cy="50" r="35" fill="none" stroke="rgb(15 23 42)" strokeWidth="4" />
      ) : (
        <polygon points={optionPolygonPoints(shape)} fill="none" stroke="rgb(15 23 42)" strokeWidth="4" strokeLinejoin="round" />
      )}
    </svg>
  );
}

function ImageOptionRenderer({ option, source }: { option: NormalizedOption; source: QuestionOption }) {
  const [failed, setFailed] = useState(false);
  const src = typeof option.visual.src === "string" ? option.visual.src : undefined;
  const alt = typeof option.visual.alt === "string" ? option.visual.alt : `Επιλογή ${source.id}`;

  if (!src || failed) {
    if (!src) warnInvalidOption(source, "image option is missing visual.src");
    return <UnavailableOption />;
  }

  return <img src={src} alt={alt} className="max-h-28 w-full rounded-md object-contain" loading="lazy" onError={() => setFailed(true)} />;
}

function UnavailableOption() {
  return <span className="text-sm font-semibold text-slate-300">Μη διαθέσιμη επιλογή</span>;
}

export function AnswerOptionRenderer({ option, imageSrc }: { option: QuestionOption; imageSrc?: string }) {
  if (imageSrc) {
    return <ImageOptionRenderer option={{ type: "image", label: option.label, visual: { src: imageSrc, alt: `Επιλογή ${option.id}` } }} source={option} />;
  }

  const normalized = normalizeOption(option);

  if (normalized.type === "text") return <TextOptionRenderer option={normalized} />;
  if (normalized.type === "number") return <NumberOptionRenderer option={normalized} />;
  if (normalized.type === "symbolChoice") return <SymbolChoiceOptionRenderer option={normalized} />;
  if (normalized.type === "shape") return <ShapeOptionRenderer option={normalized} source={option} />;
  if (normalized.type === "gridPosition") return <GridPositionOptionRenderer option={normalized} source={option} />;
  if (normalized.type === "dotTile") return <DotTileOptionRenderer option={normalized} source={option} />;
  if (normalized.type === "dotBlock") return <DotBlockOptionRenderer option={normalized} source={option} />;
  if (normalized.type === "circleHalfFill") return <CircleHalfFillOptionRenderer option={normalized} />;
  if (normalized.type === "circleSegmentUnion") return <CircleSegmentUnionOptionRenderer option={normalized} />;
  if (normalized.type === "pieSlice") return <PieSliceOptionRenderer option={normalized} />;
  if (normalized.type === "topViewGrid") return <TopViewGridOptionRenderer option={normalized} source={option} />;
  if (normalized.type === "symbolGridCard") return <SymbolGridCardOptionRenderer option={normalized} source={option} />;
  if (normalized.type === "blueCircleDotCard") return <BlueCircleDotOptionRenderer option={normalized} />;
  if (normalized.type === "colorGridTile") return <ColorGridTileOptionRenderer option={normalized} source={option} />;
  if (normalized.type === "roundedDome") return <RoundedDomeOptionRenderer option={normalized} />;
  if (normalized.type === "quadrantColorShape") return <QuadrantColorShapeOptionRenderer option={normalized} />;
  if (normalized.type === "miniGridSymbolCycle") return <MiniGridSymbolCycleOptionRenderer option={normalized} />;
  if (normalized.type === "miniGridSymbol") return <MiniGridSymbolOptionRenderer option={normalized} />;
  if (normalized.type === "hexCross") return <HexCrossOptionRenderer option={normalized} />;
  if (normalized.type === "blockGrid") return <BlockGridOptionRenderer option={normalized} source={option} />;
  if (normalized.type === "tileSplit") return <TileSplitOptionRenderer option={normalized} />;
  if (normalized.type === "lineTile") return <LineTileOptionRenderer option={normalized} />;
  if (normalized.type === "nestedShape") return <NestedShapeOptionRenderer option={normalized} />;
  if (normalized.type === "quadrantShape") return <QuadrantShapeOptionRenderer option={normalized} />;
  if (normalized.type === "figurePiece") return <FigurePieceOptionRenderer option={normalized} />;
  if (normalized.type === "image") return <ImageOptionRenderer option={normalized} source={option} />;

  warnInvalidOption(option, "unknown option type");
  return <UnavailableOption />;
}
